import { getUser, getUserAthletes, listenToUserChanges, updateUser } from '@supa/users';
import { useEffect, useMemo, useState } from 'react';
import { stringFromError } from '@utils/utils';
import { log, logError, setSentryUser } from '@utils/logging';
import useAuth from '@hooks/useAuth';
import { UserContext, UserContextValues } from '@hooks/useUser';
import { STATUS } from '@type/db-types';
import { initializeStripeInfo } from '@modules/api';
import useLatestCallback from 'use-latest-callback';

export default function UserProvider({
    children,
}: {
    children: React.JSX.Element;
}): React.JSX.Element {
    const [user, setUser] = useState<UserContextValues['user']>();
    const [athletes, setAthletes] = useState<UserContextValues['athletes']>([]);
    const { authUser, loading: authLoading } = useAuth();

    const refresh = useLatestCallback(async () => {
        try {
            if (authUser?.id) {
                const [newUser, newAthletes] = await Promise.all([
                    getUser(authUser.id),
                    getUserAthletes(authUser.id),
                ]).catch((e) => {
                    logError(e);
                    // if there is an error, return null for both user and athletes
                    return [null, [] as UserContextValues['athletes']] as const;
                });

                setAthletes(newAthletes);
                setUser(newUser || null);
                setSentryUser(
                    newUser || authUser.id ? { id: authUser.id, email: authUser.email } : null,
                );

                // we are having an issue with the stripe account not being set up when the event/team modal opens
                // for now, initializing it ahead of time should fix the issue...
                initializeStripeInfo(authUser?.id);
            } else if (!authLoading) {
                // no auth user, set user as done loading and null
                setUser(null);
            }
        } catch (e) {
            // no auth user, set user as done loading and null
            setUser(null);
            log(`[fetchUser]: ${stringFromError(e)}`);
        }
    });

    useEffect(() => {
        // fetch user once auth has loaded
        refresh();
        // eslint-disable-next-line react-compiler/react-compiler
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, authUser?.id]);

    // Always listen to user changes if there is an auth user
    useEffect(() => {
        if (authUser?.id) return listenToUserChanges(authUser.id, setUser);
    }, [authUser?.id]);

    useEffect(() => {
        if (user?.status === STATUS.INVITED) {
            // update invited status once a user logs in
            updateUser({ id: user.id, status: STATUS.ACTIVE }, 'auto-set status').catch(logError);
        }
    }, [user?.id, user?.status]);

    const userContext: UserContextValues = useMemo(() => {
        return {
            athletes,
            loading: user === undefined,
            user,
            refresh,
        };
    }, [athletes, refresh, user]);

    return <UserContext.Provider value={userContext}>{children}</UserContext.Provider>;
}
